import { player } from '../../entities/athlete/player'
import {
  AudioNames,
  AudioGroups,
  MovementInFinishPhases,
  type DisciplinePhaseManager
} from '../../types'
import { finishPhaseConfig } from '../../config'
import {
  timeManager,
  playersManager,
  THREE,
  fpsManager,
  modes,
  corePhasesManager,
  gsap,
  audioManager,
  TimesTypes,
  trainingManager,
  minigameConfig
} from '@powerplay/core-minigames'
import { endManager } from '@/app/EndManager'
import { opponentsManager } from '@/app/entities/athlete/opponent/OpponentsManager'
import { tutorialFlow } from '@/app/modes/tutorial/TutorialFlow'
import { startPhaseStateManager } from '../StartPhase/StartPhaseStateManager'
import type { Athlete } from '@/app/entities/athlete'
import {
  tableState,
  inputsState,
  gamePhaseState,
  uiState,
  trainingState,
  emotionMessagesState
} from '@/stores'
import { waitingState } from '@powerplay/core-minigames-ui-ssm'

/**
 * Trieda fazy pre dojazd v cieli (resp naburanie)
 */
export class FinishPhaseManager implements DisciplinePhaseManager {

  /** Ci su skipovatelne hlasky pri konecnej emocii */
  public emotionMessagesSkippable = false

  /** Freeznutie zaznamenavania superov pri dennej lige, lebo pouzivame fake superov */
  public dailyLeagueSetResultsOpponentsFreeze = false

  /** callback na zavolanie po skonceni fazy */
  private callbackEnd: () => unknown

  /** tween na ukoncenie fazy po animacii */
  private finishPhaseTween !: gsap.core.Tween

  /** ci faza skoncila */
  private ended = false

  /** kolko framov preslo od zaciatku fazy */
  private framesInPhase = 0

  /** kolko framov preslo od zaciatku podfazy movementu */
  private framesInLocalPhase = 0

  /** audio divakov podla emocie */
  private audienceAudio !: AudioNames

  /** Aktualna rychlost pohybu */
  private actualSpeed = 0

  /** Aktualny step v linearnej faze pohybu */
  private linearDecreaseStep = 0

  /** Aktualna faza pohybu v cieli */
  private movementInFinishPhase = MovementInFinishPhases.before

  /** pole objektov faz pohybu v cieli */
  private movementPhasesUpdates: (() => unknown)[] = []

  /** tween na zmenu UI stavu */
  private changeUiStateTween!: gsap.core.Tween

  /** tween na audio komentatorov */
  private commentatorsAudioDelayTween!: gsap.core.Tween

  /** ci je mozne skipnut */
  private skippable = false

  /** kolko po starte mame zobrazit finish top box */
  private SHOW_FINISH_TOP_BOX_SECONDS = 1.5

  /** kolko po starte mame zahrat komentatorov - musi byt mensie cislo ako na zobrazenie finish top box */
  private COMMENTATORS_AUDIO_DELAY = 0.5

  /** Kolko superov dobehlo do ciela */
  public finishedOpponents = 0

  /** tween na emocii */
  private emotionTween?: gsap.core.Tween

  /** tween pre unlock skipu finalnej emocie */
  private tweenToSkipFinalEmotions?: gsap.core.Tween

  /** Ci uz su emocie hotove */
  private emotionsDone = false

  /** Ci je hrac v top3 */
  private inTop3 = false

  /** Ci mal hrac PB */
  private isPersonalBest = false

  /** Ci mal hrac WR */
  private isWorldRecord = false

  /** Hodnota PB */
  private personalBest = 0

  /** Hodnota WR */
  private worldRecord = 0

  /** Finalna pozicia hraca */
  private position = 0

  /** Finalny cas hraca v sekundach */
  private timeSeconds = 0

  /** pomocny vektor */
  private tempVector = new THREE.Vector3()

  /** Ci sa podarilo odoslat */
  public sendSaveResultDone = false

  /**
   * Konstruktor
   */
  public constructor(callbackEnd: () => unknown) {

    this.callbackEnd = callbackEnd

  }

  /**
   * Pripravenie fazy
   */
  public preparePhase = (): void => {

    // nastavime fazy pohybu
    this.movementPhasesUpdates[MovementInFinishPhases.setThings] = this.moveInFinishSetThings
    this.movementPhasesUpdates[MovementInFinishPhases.nonLinearDecrease] =
            this.moveInFinishNonLinear
    this.movementPhasesUpdates[MovementInFinishPhases.linearDecrease] = this.moveInFinishLinear
    this.movementPhasesUpdates[MovementInFinishPhases.stay] = this.moveInFinishStay

  }

  /**
   * Start fazy
   */
  public startPhase = (): void => {

    if (modes.isTutorial()) {

      player.finishAction()
      tutorialFlow.finishPassed()
      return

    }

    fpsManager.pauseCounting()

    this.reset()
    this.preparePhase()

    playersManager.setStandings()
    console.log('STANDINGS', playersManager.getStandings())
    tableState().dataTable = playersManager.getStandings()

    console.warn('finish phase started')

    inputsState().isVisible = false
    gamePhaseState().showSmallActionButtons = false

    player.finishAction()

    // this.actualSpeed = player.velocityManager.getVelocity() / fpsManager.fpsLimit
    console.log(`Finish speed ${this.actualSpeed}`)

    this.movementInFinishPhase = MovementInFinishPhases.nonLinearDecrease

    uiState().$patch({
      showTimeKeeper: true,
      showTrainingLayout: modes.isTrainingMode(),
      isTraining: modes.isTrainingMode()
    })

    this.commentatorsAudioDelayTween = gsap.to({}, {
      duration: this.COMMENTATORS_AUDIO_DELAY,
      onComplete: () => {

        this.playAudioCommentatorsFinish()

      }
    })

    this.changeUiStateTween = gsap.to({}, {
      duration: this.SHOW_FINISH_TOP_BOX_SECONDS,
      onComplete: () => {

        uiState().$patch({
          showTimeKeeper: true,
          showTrainingLayout: modes.isTrainingMode(),
          isTraining: modes.isTrainingMode()
        })
        this.prepareEmotionMessages()

      }
    })

  }

  /**
   * Zahratie komentatorov na konci
   */
  private playAudioCommentatorsFinish(): void {

    if (
      corePhasesManager.disciplineActualAttempt < corePhasesManager.disciplineAttemptsCount ||
      modes.isTrainingMode()
    ) return

    // setneme si data a stats pre audio aj pre neskor emotion messages
    this.setFinalDataAndStats()
    this.playCommentatorAudio()

  }

  /**
   * nastavime data pre top box
   */
  private prepareEmotionMessages(): void {

    if (corePhasesManager.disciplineActualAttempt < corePhasesManager.disciplineAttemptsCount) return

    let withEmotionMessages = false

    // UI pri emocii, ak islo o posledny pokus
    if (modes.isTrainingMode()) {

      // HIGH SCORE - TRENING
      trainingState().$patch({
        showLeftBoxes: false,
        newHighScore: Math.ceil(trainingManager.getNewPotentialHighScore()),
        showNewHighScore: trainingManager.isNewHighScore()
      })

    } else {

      // UI veci mimo TG
      withEmotionMessages = this.showEmotionMessages()

    }

    this.setTimerToSkipFinalEmotions()

    if (withEmotionMessages) return

    this.emotionTween = gsap.to(
      {},
      {
        duration: 2.5,
        onComplete: () => {

          this.tweenToSkipFinalEmotions?.kill()
          this.afterEmotions()

        }
      }
    )

  }

  /**
   * Spravenie veci po emociach
   */
  public afterEmotions(): void {

    trainingState().showNewHighScore = false
    emotionMessagesState().showMessage = false

    this.emotionsDone = true

  }

  /**
   * Nastavenie timeru na skip final emocie
   */
  private setTimerToSkipFinalEmotions(): void {

    // dame timer na skip poslednej emocie s hlaskami
    this.tweenToSkipFinalEmotions = gsap.to({}, {
      duration: 1.5,
      onComplete: () => {

        this.emotionMessagesSkippable = true

      }
    })

  }

  /**
   * Nastavenie finalnych dat a statistik
   */
  private setFinalDataAndStats(): void {

    playersManager.setStandings()
    this.timeSeconds = Math.ceil(player.finalTime * 100) / 100
    this.personalBest = playersManager.getPlayer().personalBest
    if (this.personalBest === 0) this.personalBest = minigameConfig.dnfValue
    this.worldRecord = endManager.worldRecord
    this.position = playersManager.getPlayerActualPosition(player.uuid)
    this.inTop3 = this.position <= 3
    this.isPersonalBest = modes.isTournament() ? false : this.timeSeconds <= this.personalBest
    this.isWorldRecord = modes.isTournament() ? false : this.timeSeconds <= this.worldRecord

  }

  /**
   * nastavime emocne message
   * @returns True, ak zobrazujeme emocnu hlasku
   */
  public showEmotionMessages(): boolean {

    // ak mame nejaku hlasku/y, tak zobrazime kameru kusok inak
    if (
      ((modes.isDailyLeague() || modes.isTournament()) && !playersManager.isPlayerImproved()) ||
      (!this.inTop3 && !this.isPersonalBest && !this.isWorldRecord)
    ) return false

    startPhaseStateManager.hideAllTextMessages()

    let recordText = ''
    let recordValue = ''
    if (this.isPersonalBest) {

      recordText = this.timeSeconds < this.personalBest ? 'newPersonalBest' : 'personalBest'
      recordValue = timeManager.getTimeInFormatFromSeconds(this.timeSeconds)

    }
    if (this.isWorldRecord) {

      recordText = this.timeSeconds < this.worldRecord ? 'newWorldRecord' : 'worldRecord'
      recordValue = timeManager.getTimeInFormatFromSeconds(this.timeSeconds)

    }

    emotionMessagesState().$patch({
      showMessage: true,
      firstLineText: this.inTop3 ? 'congratulations' : '',
      firstLineTextSecond: recordText,
      secondLineText: this.inTop3 ? this.position : 0,
      secondLineTextSecond: recordValue,
    })

    // dame timer na skip poslednej emocie s hlaskami
    this.setTimerToSkipFinalEmotions()

    return true

  }

  private getAthleteObjectByUUID(uuid: string): Athlete {

    if (player.uuid === uuid) return player

    const index = opponentsManager.getOpponentsIds().findIndex(id => id === uuid)
    return opponentsManager.getOpponents()[index]

  }

  /**
   * pustime komentatora
   */
  private playCommentatorAudio(): void {

    let audio = AudioNames.commentatorRank6
    const standings = playersManager.getStandings()
    const photoFinishTimeLimit = 0.03

    if (this.position <= 3) {

      // aby nepadla cela hra, radsej obetujeme zvuky
      if (standings[0] === undefined || standings[1] === undefined) return

      const athleteFirst = this.getAthleteObjectByUUID(standings[0].uuid)
      const athleteSecond = this.getAthleteObjectByUUID(standings[1].uuid)

      // aby nepadla cela hra, radsej obetujeme zvuky
      if (athleteFirst === undefined || athleteSecond === undefined) return

      const firstTime = athleteFirst.finalTime
      const secondTime = athleteSecond.finalTime
      let photoFinish = Math.abs(secondTime - firstTime) <= photoFinishTimeLimit

      // iba ked mame zaznam o tretom superovi, mozeme ho kontrolovat
      if (standings[2]) {

        const athleteThird = this.getAthleteObjectByUUID(standings[2].uuid)
        if (athleteThird !== undefined) {

          const thirdTime = athleteThird.finalTime
          photoFinish = photoFinish || Math.abs(thirdTime - firstTime) <= photoFinishTimeLimit

        }

      }

      // ak by bol diff 0, tak
      if (photoFinish) {

        audio = AudioNames.commentatorRank123

      } else {

        audio = AudioNames.commentatorRank23
        if (this.position === 1) audio = AudioNames.commentatorRank1

      }

    } else if (
      this.position <= ((modes.isDailyLeague() || modes.isTournament() || modes.isBossCompetition()) ? 10 : 5)
    ) {

      audio = AudioNames.commentatorRank45

    }

    // pri boss fighte a 2. mieste davame speci hlasku
    if (modes.isEventBossFight() && this.position > 1) audio = AudioNames.commentatorRank6

    if (this.isPersonalBest) audio = AudioNames.commentatorPersonalBest
    if (this.isWorldRecord) audio = AudioNames.commentatorWorldRecord

    audioManager.stopAudioByGroup(AudioGroups.commentators)
    audioManager.play(audio)

  }

  /**
   * Hybanie sa v cieli - faza nastavenie veci
   */
  private moveInFinishSetThings = (): void => {

    const playerPosition = player.getPosition()

    this.tempVector.set(
      playerPosition.x,
      playerPosition.y,
      playerPosition.z + 2
    )

    player.athleteObject.lookAt(this.tempVector)

    this.movementInFinishPhase = MovementInFinishPhases.nonLinearDecrease

  }

  /**
   * Hybanie sa v cieli - faza nelinearny pohyb
   */
  private moveInFinishNonLinear = (): void => {

    this.framesInLocalPhase++

    const { coef, everyXFrames, frames } = finishPhaseConfig.nonLinearDecreasingSpeed

    // kazdych x frameov menime rychlost
    if (this.framesInLocalPhase % everyXFrames === 1) this.actualSpeed *= coef

    // check konca
    if (this.framesInLocalPhase === frames) {

      this.movementInFinishPhase = MovementInFinishPhases.linearDecrease
      this.framesInLocalPhase = 0
      this.linearDecreaseStep = this.actualSpeed /
                finishPhaseConfig.linearDecreasingSpeed.frames

    }

  }

  /**
   * Hybanie sa v cieli - faza linearny pohyb
   */
  private moveInFinishLinear = (): void => {

    this.framesInLocalPhase++

    this.actualSpeed -= this.linearDecreaseStep
    if (this.actualSpeed < 0) this.actualSpeed = 0

    // check konca
    if (this.framesInLocalPhase === finishPhaseConfig.linearDecreasingSpeed.frames) {

      this.movementInFinishPhase = MovementInFinishPhases.stay
      this.framesInLocalPhase = 0

    }

  }

  /**
   * Hybanie sa v cieli - faza statie
   */
  private moveInFinishStay = (): void => {

    this.framesInLocalPhase++

  }

  /**
   * Pohyb v cieli
   */
  private moveInFinish(): void {

    this.framesInPhase++

    // po x frameoch nastavujeme animaciu konca
    if (this.framesInPhase === finishPhaseConfig.startAnimationEndAfterFrames) {

      /*
       * finishPhase.setFinishPhaseTween()
       */

    }

    // update aktualnej fazy
    this.movementPhasesUpdates[this.movementInFinishPhase]?.()

  }

  /**
   * Kontrola a riesenie inputov
   */
  public handleInputs(): void {

    // skip na emocie
    if (this.emotionMessagesSkippable && this.allOpponentsInFinish()) {

      this.emotionTween?.kill()
      this.emotionMessagesSkippable = false
      this.afterEmotions()

      return

    }

  }

  /**
   * Aktualizovanie fazy
   */
  public update = (): void => {

    this.moveInFinish()
    this.checkEnd()

  }

  /**
   * Zistenie, ci su vsetci superi v cieli
   * @returns True, ak su vsetci superi v cieli
   */
  private allOpponentsInFinish(): boolean {

    return this.finishedOpponents === opponentsManager.getOpponentsCount()

  }

  /**
   * Kontrola konca
   */
  private checkEnd(): void {

    // ak je hrac uz v cieli a cakame na protihracov, aby sa uz dalo skipnut
    if (this.allOpponentsInFinish() && this.emotionsDone) {

      this.setFinishPhaseTween()

    }

  }

  /**
   * Zobrazenie finalnej tabulky
   */
  public prepareFinishTable() {

    this.setTournamentTableSettings()
    this.generateFakeOpponentsData()
    timeManager.setActive(TimesTypes.game, false)
    // data pre tabulku
    playersManager.setStandings()
    console.log('STANDINGS', playersManager.getStandings())
    tableState().dataTable = playersManager.getStandings()

  }

  /**
   * Vygenerovanie casy pre hracov ktori nestihli prejst cielom
   */
  private generateFakeOpponentsData(): void {

    if (!modes.isDailyLeague()) return

    // musime nastavit, aby sa uz ziadne data nezapisovali dalsie
    this.dailyLeagueSetResultsOpponentsFreeze = true

    // kvoli dennej lige musime dat naspat originalnych superov, aby sa zobrazili v konecnej listine
    opponentsManager.setOriginalData()

  }

  /**
   * Nastavenie dat o hracoch
   */
  private setTournamentTableSettings(): void {

    if (!modes.isTournament()) return
    waitingState().isWaiting = true
    tableState().$patch({
      showTable: true,
      resultText: 'provisionalResults',
      activeState: false,
      dataTable: [],
      isStartList: false,
    })

  }

  /**
   * Ukoncene fazy
   * @param type - Typ ukoncenia
   */
  public finishPhase = (): void => {

    this.prepareFinishTable()
    if (this.ended) return
    this.ended = true

    this.emotionMessagesSkippable = false

    if (this.finishPhaseTween) this.finishPhaseTween.kill()
    if (this.changeUiStateTween) this.changeUiStateTween.kill()
    if (this.commentatorsAudioDelayTween) this.commentatorsAudioDelayTween.kill()

    uiState().$patch({
      showTimeKeeper: false,
      showTrainingLayout: false,
      isTraining: modes.isTrainingMode(),
      showWind: false
    })

    // resetujeme vsetky hlasky, aby nahodou neostali
    startPhaseStateManager.hideTextMessage(1)
    startPhaseStateManager.hideTextMessage(2)

    fpsManager.pauseCounting()
    endManager.sendLogEnd()
    endManager.sendSaveResult()


    gamePhaseState().showRecords = false

    console.warn('finish phase ended')
    this.callbackEnd()

  }

  /**
   * sets tween to finish phase
   */
  public setFinishPhaseTween(): void {

    this.finishPhase()

  }

  /**
   * reset fazy
   */
  public reset(): void {

    this.ended = false
    this.movementInFinishPhase = MovementInFinishPhases.before
    if (this.changeUiStateTween) this.changeUiStateTween.kill()
    if (this.commentatorsAudioDelayTween) this.commentatorsAudioDelayTween.kill()
    this.emotionMessagesSkippable = false
    this.emotionTween?.kill()
    this.emotionsDone = false

  }

}
